/**
 * Utility functions for CDP SDK.
 */

/** Format data as indented JSON string. */
export function formatJson(data: unknown, indent = 2): string {
  return JSON.stringify(data, null, indent);
}

/** Parse datetime string to Date object. */
export function parseDatetime(dtStr: string): Date {
  const formats = [
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/,
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/,
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/,
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/,
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/,
  ];

  const d = new Date(dtStr);
  if (!isNaN(d.getTime())) {
    return d;
  }

  for (const fmt of formats) {
    if (fmt.test(dtStr)) {
      const clean = dtStr.replace(' ', 'T');
      const parsed = new Date(clean);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  throw new Error(`Cannot parse datetime: ${dtStr}`);
}

/** Truncate string to max length. */
export function truncateString(s: string, maxLength = 50, suffix = '...'): string {
  if (s.length <= maxLength) return s;
  return s.slice(0, maxLength - suffix.length) + suffix;
}

/** Format file size in human-readable format. */
export function formatFileSize(sizeBytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = sizeBytes;
  for (const unit of units) {
    if (size < 1024.0) {
      return `${size.toFixed(2)} ${unit}`;
    }
    size /= 1024.0;
  }
  return `${size.toFixed(2)} PB`;
}

/** Format duration in human-readable format. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

/** Extract UUID from a reference string like "uuid:field". */
export function extractUuidFromRef(ref: string): string {
  if (ref.includes(':')) {
    return ref.split(':')[0];
  }
  return ref;
}

/** Build options object string for CDP list APIs. */
export function buildOptionsString(options: Record<string, string | number | undefined>): string {
  const filtered: Record<string, string | number> = {};
  for (const key of Object.keys(options)) {
    const value = options[key];
    if (value !== undefined) {
      filtered[key] = value;
    }
  }
  return JSON.stringify(filtered);
}

/** Convert data to markdown table. */
export function tableToMarkdown(headers: string[], rows: Array<Array<string | number>>): string {
  if (!rows.length) return '';

  const widths = headers.map((h, i) => {
    let w = h.length;
    for (const row of rows) {
      w = Math.max(w, String(row[i] ?? '').length);
    }
    return w;
  });

  const lines: string[] = [];
  lines.push(`| ${headers.map((h, i) => h.padEnd(widths[i])).join(' | ')} |`);
  lines.push(`| ${widths.map((w) => '-'.repeat(w)).join(' | ')} |`);
  for (const row of rows) {
    lines.push(`| ${row.map((cell, i) => String(cell ?? '').padEnd(widths[i])).join(' | ')} |`);
  }
  return lines.join('\n');
}

/** Sanitize filename for safe usage. */
export function sanitizeFilename(filename: string): string {
  const invalidChars = '<>:"/\\|?*';
  let result = filename;
  for (const char of invalidChars) {
    result = result.replace(new RegExp(`\\${char}`, 'g'), '_');
  }
  return result.replace(/^[.\s]+|[.\s]+$/g, '');
}

/** Basic UUID format validation. */
export function validateUuid(uuid: string): boolean {
  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return pattern.test(uuid);
}

/** Deep merge two objects. */
export function mergeDicts(base: Record<string, any>, override: Record<string, any>): Record<string, any> {
  const result = { ...base };
  for (const key of Object.keys(override)) {
    const val = override[key];
    if (key in result && typeof result[key] === 'object' && result[key] !== null && typeof val === 'object' && val !== null && !Array.isArray(val)) {
      result[key] = mergeDicts(result[key], val);
    } else {
      result[key] = val;
    }
  }
  return result;
}

/** Convert a base64 data URI to Blob. */
export function base64ToBlob(base64Data: string, contentType = 'image/jpeg'): Blob {
  const byteString = atob(base64Data.split(',')[1] || base64Data);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: contentType });
}
